// 등급제 v18 — 월 리셋 31일 창 + 유효 조회수 화면 문구.
//
// 설계: projects/html-report-production/projects/creator-tier-system/ALGORITHM-SPEC-v17-DRAFT.md §10
// DB 구현: docs/tier-system/internal-policy.md §11-11 (스위치는 `creator_tier_config.window_mode`,
// RPC `admin_set_creator_tier_window_mode('monthly'|'decay')`).
//
// 크리에이터 화면은 `creator_tier_config`를 읽을 수 없다(어드민 전용 행, RLS로 막힘) — 그래서
// 서버 설정을 실시간으로 따라가는 대신 **빌드 상수**로 "지금 화면이 어느 창 방식을 전제로
// 문구를 그리는지"를 고정한다. 어드민이 DB 스위치를 `decay`로 되돌리면(항의 대응 레버,
// §10-3) 이 상수도 함께 `'decay'`로 바꿔야 화면 문구가 실제 산정과 다시 맞는다 — 자동 동기화가
// 아니므로 전환 시 이 한 줄을 잊지 않는다.
//
// 이 파일의 함수는 전부 **순수 함수**(mode → 문자열)다 — 훅도, Supabase 호출도 없다. 화면 쪽
// (MyTier.jsx, creatorTierMetrics.js, TierStateCards.jsx)은 인자 없이 불러 빌드 상수 기본값을
// 쓰거나, 필요하면 mode를 직접 넘겨 두 모드를 나란히 렌더할 수도 있다(현재 호출부는 전부 기본값).
import { tt } from "../i18n/tt";

// 'monthly'(31일 창, 감쇠 없음, 유효 조회수) | 'decay'(v16/v17 전체 이력 + 반감기 120일, 공개 조회수).
// decay로 되돌릴 때는 이 값만 바꾸면 된다 — 아래 함수들은 전부 이 상수를 기본 mode로 쓴다.
export const TIER_WINDOW_MODE = "monthly";

export function isMonthlyWindow(mode = TIER_WINDOW_MODE) {
  return mode === "monthly";
}

// 4축 카드 근거 라벨·레이더 축 라벨에 쓰는 "총 조회수" 표현.
export function totalViewsLabel(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.totalViewsLabel", "최근 31일 유효 조회수")
    : tt("a.803a9cbe", "총 조회수");
}

// 4축 카드 근거 라벨·레이더 축 라벨에 쓰는 "편당 조회수" 표현.
export function perVideoViewsLabel(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.perVideoViewsLabel", "편당 유효 조회수")
    : tt("a.b0db39dc", "편당 조회수");
}

// 성과 축 ⓘ 인포 텍스트(TIER_AXIS_INFO_TEXT.performance). 2026-09-21 사용자 지시 — 조회수
// 원천·수집 지연("유튜브 스튜디오 유효 조회수 기준, 수집 지연으로 약 4일 전까지 반영") 문구는
// 크리에이터 화면 어디에도 넣지 않는다(라벨은 "유효 조회수"까지만, 지연·원천 설명은 비노출).
// 어드민 패널의 창 범위 표시(TierModePanel.jsx)는 이 제약과 무관 — 그대로 유지한다.
export function performanceAxisInfoText(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.performanceInfo", "작품 영상의 최근 31일 유효 조회수(편당·전체)를 함께 봐요.")
    : tt("a.2b501c07", "작품 영상의 편당 조회수와 전체 조회수를 함께 봐요. 최근 성과일수록 더 크게 반영돼요.");
}

// 만족도 축 ⓘ 인포 텍스트 — decay 모드 문구의 "최근 검수일수록 더 크게 반영돼요"(감쇠 설명)는
// monthly 모드(창 안 건수만 세고 가중치 없음, 설계 §10-3/내부 정책 §11-11-2)에서는 사실과 달라
// "최근 31일 활동 기준"으로 교체한다.
export function satisfactionAxisInfoText(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.satisfactionInfo", "검수에서 수정 요청 없이 한 번에 통과하는 정도를 봐요. 최근 31일 활동 기준이에요.")
    : tt("a.673546d2", "검수에서 수정 요청 없이 한 번에 통과하는 정도를 봐요. 최근 검수일수록 더 크게 반영돼요.");
}

// 꾸준함 축 ⓘ 인포 텍스트 — decay 모드의 "최근 6개월"(26주 이력 감쇠 기준)도 monthly 모드에서는
// 31일 활동 창 기준으로 바뀌어 기간 표현이 달라진다.
export function consistencyAxisInfoText(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.consistencyInfo", "최근 31일 동안 얼마나 규칙적으로 업로드했는지를 봐요.")
    : tt("a.5b6f98b1", "최근 6개월 동안 얼마나 규칙적으로 업로드했는지를 봐요.");
}

// deriveTierAxes()의 performanceBasisLabel — "편당 {{v0}}회 · 총 {{v1}}회" 근거 문구. 숫자는
// 호출부가 formatKoreanCount()로 이미 포맷해 넘긴다(이 파일은 숫자 포맷을 모른다).
export function performanceBasisLabel(perVideoViewsText, totalViewsText, mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.performanceBasis", "최근 31일 유효 — 편당 {{v0}}회 · 총 {{v1}}회", {
        v0: perVideoViewsText,
        v1: totalViewsText,
      })
    : tt("a.5f510f97", "편당 {{v0}}회 · 총 {{v1}}회", { v0: perVideoViewsText, v1: totalViewsText });
}

// "OO까지 남은 것" 카드의 성과 축 개선 문구(AXIS_IMPROVEMENT_TEXT.performance).
export function performanceImprovementText(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.performanceImprovement", "편당 유효 조회수 추세를 꾸준히 유지해보세요")
    : tt("a.692af5c6", "편당 조회수 추세를 꾸준히 유지해보세요");
}

// AxesPreviewCard(TierStateCards.jsx, 루키/휴면 "정식 등급부터 이렇게 평가돼요" 미리보기)의
// 성과 축 한 줄 설명.
export function performanceAxisDesc(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.performanceDesc", "작품 영상의 최근 31일 유효 조회수(편당·전체)")
    : tt("a.afd377b3", "작품 영상의 편당·총 조회수");
}

// MyTier 히어로 아래 "최근 30일 흐름" 추이 차트 제목 — 창이 31일로 바뀌며 의미도 "최근 31일 창
// 이동에 따른 일별 점수"로 바뀐다. 차트 구조(TierTrendChart)는 그대로, 제목만 바뀐다.
export function trendChartTitle(mode = TIER_WINDOW_MODE) {
  return isMonthlyWindow(mode)
    ? tt("a.tier.window.trendTitle", "최근 31일 기준 일별 점수")
    : tt("a.f6c3773a", "최근 30일 흐름");
}
